package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.market.Market
import com.evoapps.evoshift.domain.staffing.LabourTarget
import kotlinx.datetime.DayOfWeek
import kotlinx.serialization.Serializable

@Serializable
data class WorkplaceFinancials(
    val fixedRevenueMonthly: Double = 0.0,
    val commissionPercent: Double = 0.0,
    val includeFixedRevenueInDailyFinancialReport: Boolean = false,
    /** Net sales target per weekday, keyed by the lowercase English day name ("monday" … "sunday"). */
    val salesTargets: Map<String, Double> = emptyMap(),
    /**
     * SH-54, SH-95: the labour share the workplace aims for: planned employer labour cost ÷ expected
     * income ([LabourShare]), in percent; null means its market's default
     * ([Market.defaultLabourPercentFor] its commission).
     */
    val labourTargetPercent: Double? = null,
    /**
     * SH-95: set only when [labourTargetPercent] was converted on reading from a target saved
     * before SH-95 as a share of net sales: that stored % of sales (the settings say so until the
     * target is saved again). Never written.
     */
    val labourTargetConvertedFromSales: Double? = null,
    /**
     * SH-120: net sales per labour hour the workplace aims for (the spreadsheet's SPMH): the sales
     * forecast turns a day's expected sales into hours and people with it; null means the
     * workplace's own figure over the last weeks.
     */
    val salesPerLabourHourGoal: Double? = null
) {
    /** The staffing hints' band: this workplace's target, or [market]'s default for its commission. */
    fun labourTarget(market: Market): LabourTarget =
        labourTargetPercent?.takeIf { it > 0 }?.let { LabourTarget(it) } ?: LabourTarget.defaultFor(market, commissionPercent)

    /** What this workplace's labour % is measured against (SH-95). */
    val labourBase: LabourBase get() = LabourShare.baseOf(commissionPercent, fixedRevenueMonthly)

    /** The target of [day]; settings saved before SH-51 used "mon" … "sun", still read. */
    fun salesTargetFor(day: DayOfWeek): Double? =
        salesTargets[salesTargetKey(day)] ?: salesTargets[salesTargetKey(day).take(3)]

    companion object {
        /**
         * SH-95: the labour target of a document saved before SH-95 ([storedPercent], a share of net
         * sales) as a share of income at [commissionPercent]: ÷ the commission, once, on reading,
         * rounded to 0.1 point.
         * Without a commission the two are the same and nothing changes.
         */
        fun labourTargetFromSales(storedPercent: Double, commissionPercent: Double): Double =
            kotlin.math.round(LabourShare.incomeEquivalent(storedPercent, commissionPercent) * 10) / 10

        fun salesTargetKey(day: DayOfWeek): String = day.name.lowercase()
    }
}
